/**
 * Simple in-memory cache for article statistics
 * Prevents excessive API calls by caching results for a short period
 */

class StatsCache {
  constructor() {
    this.cache = null;
    this.cacheTime = null;
    this.cacheDuration = 30000; // 30 seconds
    this.pendingRequest = null;
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid() {
    return this.cache !== null && 
           this.cacheTime !== null && 
           (Date.now() - this.cacheTime) < this.cacheDuration;
  }

  /**
   * Get cached stats or fetch new ones
   * @param {Function} fetchFn - Function to fetch stats from API
   * @param {boolean} force - Force refresh cache
   */
  async getStats(fetchFn, force = false) {
    // Return cached data if valid and not forcing refresh
    if (!force && this.isCacheValid()) {
      console.debug('📊 Using cached stats');
      return this.cache;
    }

    // If there's already a pending request, wait for it
    if (this.pendingRequest && !force) {
      console.debug('📊 Waiting for pending stats request');
      return this.pendingRequest;
    }

    // Make new request
    console.debug('📊 Fetching fresh stats');
    this.pendingRequest = this._fetchStats(fetchFn);
    
    try {
      const stats = await this.pendingRequest;
      return stats;
    } finally {
      this.pendingRequest = null;
    }
  }

  /**
   * Internal method to fetch and cache stats
   */
  async _fetchStats(fetchFn) {
    const stats = await fetchFn();
    this.cache = stats;
    this.cacheTime = Date.now();
    return stats;
  }

  /**
   * Invalidate cache (useful when stats should definitely be refreshed)
   */
  invalidate() {
    console.debug('📊 Invalidating stats cache');
    this.cache = null;
    this.cacheTime = null;
  }

  /**
   * Get cache status for debugging
   */
  getStatus() {
    return {
      hasCache: this.cache !== null,
      cacheAge: this.cacheTime ? Date.now() - this.cacheTime : null,
      isValid: this.isCacheValid(),
      hasPendingRequest: this.pendingRequest !== null
    };
  }
}

// Export singleton instance
export const statsCache = new StatsCache();
export default statsCache;